/**
 * Code based on
 * Jonas Raoni Soares Silva
 * http://jsfromhell.com/classes/binary-parser [rev. #1]
 */

// version modifiée pour esm (et passer le build)
// cf commits avant 2024-03-28 pour avoir les anciennes versions de ce fichier

// Cf également la version modifiée pour DH3DLibrary sur http://magicien.github.io/DH3DLibrary/file/js/third_party/BinaryParser.js.html

// Voir aussi https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/Sending_and_Receiving_Binary_Data
// pour éventuellement se passer d'une telle lib en utilisant les objets js natifs Blob & Uint8Array

/* eslint-disable one-var, semi, eqeqeq, no-cond-assign */

/**
 * Fonction servant à encoder des entiers ou float dans ou depuis un flux de données binaires.
 * @constructor
 */
function BinaryParser (bigEndian, allowExceptions) {
  this.bigEndian = bigEndian
  this.allowExceptions = allowExceptions
}

BinaryParser.prototype.encodeFloat = function (number, precisionBits, exponentBits) {
  var bias = Math.pow(2, exponentBits - 1) - 1,
    minExp = -bias + 1,
    maxExp = bias,
    minUnnormExp = minExp - precisionBits,
    status = isNaN(n = parseFloat(number)) || n == -Infinity || n == +Infinity ? n : 0,
    exp = 0,
    len = 2 * bias + 1 + precisionBits + 3,
    bin = new Array(len),
    signal = (n = status !== 0 ? 0 : n) < 0,
    n = Math.abs(n),
    intPart = Math.floor(n),
    floatPart = n - intPart,
    i,
    lastBit,
    rounded,
    j,
    result,
    r;
  for (i = len; i; bin[--i] = 0);
  for (i = bias + 2; intPart && i; bin[--i] = intPart % 2, intPart = Math.floor(intPart / 2));
  for (i = bias + 1; floatPart > 0 && i; (bin[++i] = ((floatPart *= 2) >= 1) - 0) && --floatPart);
  for (i = -1; ++i < len && !bin[i];);
  if (bin[(lastBit = precisionBits - 1 + (i = (exp = bias + 1 - i) >= minExp && exp <= maxExp ? i + 1 : bias + 1 - (exp = minExp - 1))) + 1]) {
    if (!(rounded = bin[lastBit])) {
      for (j = lastBit + 2; !rounded && j < len; rounded = bin[j++]);
    }
    for (j = lastBit + 1; rounded && --j >= 0; (bin[j] = !bin[j] - 0) && (rounded = 0));
  }
  for (i = i - 2 < 0 ? -1 : i - 3; ++i < len && !bin[i];);

  (exp = bias + 1 - i) >= minExp && exp <= maxExp ? ++i : exp < minExp && (exp != bias + 1 - len && exp < minUnnormExp && this.warn('encodeFloat::float underflow'), i = bias + 1 - (exp = minExp - 1)); // eslint-disable-line eqeqeq
  (intPart || status !== 0) && (this.warn(intPart ? 'encodeFloat::float overflow' : 'encodeFloat::' + status),
  exp = maxExp + 1, i = bias + 2, status == -Infinity ? signal = 1 : isNaN(status) && (bin[i] = 1));
  for (n = Math.abs(exp + bias), j = exponentBits + 1, result = ''; --j; result = (n % 2) + result, n = n >>= 1);
  for (n = 0, j = 0, i = (result = (signal ? '1' : '0') + result + bin.slice(i, i + precisionBits).join('')).length, r = [];
    i; n += (1 << j) * result.charAt(--i), j == 7 && (r[r.length] = String.fromCharCode(n), n = 0), j = (j + 1) % 8);
  r[r.length] = n ? String.fromCharCode(n) : '';
  return (this.bigEndian ? r.reverse() : r).join('');
};
BinaryParser.prototype.encodeInt = function (number, bits, signed) {
  const max = Math.pow(2, bits), r = [];
  (number >= max || number < -(max >> 1)) && this.warn('encodeInt::overflow') && (number = 0);
  number < 0 && (number += max);
  for (; number; r[r.length] = String.fromCharCode(number % 256), number = Math.floor(number / 256));
  for (bits = -(-bits >> 3) - r.length; bits--; r[r.length] = '\0');
  return (this.bigEndian ? r.reverse() : r).join('');
};
BinaryParser.prototype.decodeFloat = function (data, precisionBits, exponentBits) {
  var b = ((b = new this.Buffer(this.bigEndian, data)).checkBuffer(precisionBits + exponentBits + 1), b),
    bias = Math.pow(2, exponentBits - 1) - 1,
    signal = b.readBits(precisionBits + exponentBits, 1),
    exponent = b.readBits(precisionBits, exponentBits),
    significand = 0,
    divisor = 2,
    curByte = b.buffer.length + (-precisionBits >> 3) - 1,
    byteValue,
    startBit,
    mask;
  do {
    for (byteValue = b.buffer[++curByte], startBit = precisionBits % 8 || 8, mask = 1 << startBit;
      mask >>= 1; (byteValue & mask) && (significand += 1 / divisor), divisor *= 2);
  } while (precisionBits -= startBit);
  return exponent == (bias << 1) + 1 ? significand ? NaN : signal ? -Infinity : +Infinity : (1 + signal * -2) * (exponent || significand ? !exponent ? Math.pow(2, -bias + 1) * significand : Math.pow(2, exponent - bias) * (1 + significand) : 0);
};
BinaryParser.prototype.decodeInt = function (data, bits, signed) {
  const b = new this.Buffer(this.bigEndian, data), x = b.readBits(0, bits), max = Math.pow(2, bits);
  return signed && x >= max / 2 ? x - max : x;
};

BinaryParser.prototype.warn = function (msg) {
  if (this.allowExceptions) throw new Error(msg);
  return 1;
};
BinaryParser.prototype.toSmall = function (data) { return this.decodeInt(data, 8, true); };
BinaryParser.prototype.fromSmall = function (number) { return this.encodeInt(number, 8, true); };
BinaryParser.prototype.toByte = function (data) { return this.decodeInt(data, 8, false); };
BinaryParser.prototype.fromByte = function (number) { return this.encodeInt(number, 8, false); };
BinaryParser.prototype.toShort = function (data) { return this.decodeInt(data, 16, true); };
BinaryParser.prototype.fromShort = function (number) { return this.encodeInt(number, 16, true); };
BinaryParser.prototype.toWord = function (data) { return this.decodeInt(data, 16, false); };
BinaryParser.prototype.fromWord = function (number) { return this.encodeInt(number, 16, false); };
BinaryParser.prototype.toInt = function (data) { return this.decodeInt(data, 32, true); };
BinaryParser.prototype.fromInt = function (number) { return this.encodeInt(number, 32, true); };
BinaryParser.prototype.toDWord = function (data) { return this.decodeInt(data, 32, false); };
BinaryParser.prototype.fromDWord = function (number) { return this.encodeInt(number, 32, false); };
BinaryParser.prototype.toFloat = function (data) { return this.decodeFloat(data, 23, 8); };
BinaryParser.prototype.fromFloat = function (number) { return this.encodeFloat(number, 23, 8); };
BinaryParser.prototype.toDouble = function (data) { return this.decodeFloat(data, 52, 11); };
BinaryParser.prototype.fromDouble = function (number) { return this.encodeFloat(number, 52, 11); };

/**
 * @param bigEndian
 * @param buffer
 * @constructor
 */
function Buffer (bigEndian, buffer) {
  this.bigEndian = bigEndian || 0
  this.buffer = []
  this.setBuffer(buffer)
}
BinaryParser.prototype.Buffer = Buffer

Buffer.prototype.readBits = function (start, length) {
  // shl fix: Henri Torgemane ~1996 (compressed by Jonas Raoni)
  function shl (a, b) {
    for (++b; --b; a = ((a %= 0x7fffffff + 1) & 0x40000000) == 0x40000000 ? a * 2 : (a - 0x40000000) * 2 + 0x7fffffff + 1);
    return a;
  }
  if (start < 0 || length <= 0) return 0;
  this.checkBuffer(start + length);
  for (
    var offsetLeft,
      offsetRight = start % 8,
      curByte = this.buffer.length - (start >> 3) - 1,
      lastByte = this.buffer.length + (-(start + length) >> 3),
      diff = curByte - lastByte,
      sum = ((this.buffer[curByte] >> offsetRight) & ((1 << (diff ? 8 - offsetRight : length)) - 1)) + (diff && (offsetLeft = (start + length) % 8) ? (this.buffer[lastByte++] & ((1 << offsetLeft) - 1)) << (diff-- << 3) - offsetRight : 0);
    diff;
    sum += shl(this.buffer[lastByte++], (diff-- << 3) - offsetRight)
  );
  return sum;
};
Buffer.prototype.setBuffer = function (data) {
  if (data) {
    for (var l, i = l = data.length, b = this.buffer = new Array(l); i; b[l - i] = data.charCodeAt(--i));
    this.bigEndian && b.reverse();
  }
};
Buffer.prototype.hasNeededBits = function (neededBits) {
  return this.buffer.length >= -(-neededBits >> 3);
};
Buffer.prototype.checkBuffer = function (neededBits) {
  if (!this.hasNeededBits(neededBits)) throw new Error('checkBuffer::missing bytes');
};

export default BinaryParser
